import axios from "axios";
import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { useHistory } from "react-router-dom";

import {
    Configuration as MsalConfiguration,
    InteractionRequiredAuthError,
    InteractionType,
    PublicClientApplication,
} from "@azure/msal-browser";
import {
    MsalAuthenticationResult,
    MsalAuthenticationTemplate,
    MsalProvider,
    useMsal,
} from "@azure/msal-react";
import { datadogRum } from "@datadog/browser-rum";

import { appConfig } from "./appConfig";
import { AuthContext } from "./AuthContext";
import { CustomNavigationClient } from "utils/customNavigationClient";
import { AppLoader } from "./AppLoader";

import "./styles.index.scss";

export let authInstance: PublicClientApplication;

function AuthStatusScreen({ children }: { children: React.ReactNode }): JSX.Element {
    return <div className="auth__screen">{children}</div>;
}

const AuthError = ({ error }: React.PropsWithChildren<MsalAuthenticationResult>): JSX.Element => (
    <AuthStatusScreen>
        <div className="auth__error-title">An Error Occurred</div>

        {error?.errorMessage ? (
            <div className="auth__error-message">{error.errorMessage}</div>
        ) : null}
    </AuthStatusScreen>
);

const AuthLoading = (): JSX.Element => (
    <AuthStatusScreen>
        <div>Loading</div>
    </AuthStatusScreen>
);

axios
    .get("/config.json")
    .then(async (res) => {
        const config = res.data;
        const isProd = config.ENVIRONMENT == "prod";

        appConfig.initalizeAppConfig({
            version: config.VERSION,
            environment: config.ENVIRONMENT,
            gaTrackingCode: config.GA_TRACKING_CODE,
            gtmTrackingCode: config.GTM_TRACKING_CODE,
        });

        const authConfiguration: MsalConfiguration = {
            auth: {
                redirectUri: "/",
                clientId: config.AAD_CLIENT_ID,
                authority: `https://login.microsoftonline.com/${config.AAD_TENANT_ID}`,
            },
            cache: {
                cacheLocation: "localStorage",
            },
        };

        authInstance = new PublicClientApplication(authConfiguration);
        await authInstance.initialize();

        const AuthProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
            const auth = useMsal();
            const [account] = auth.instance.getAllAccounts();
            const roles: string[] = [];

            if (
                account &&
                account.idTokenClaims &&
                Object.keys(account.idTokenClaims).includes("roles")
            ) {
                const claims = account.idTokenClaims as { roles: string[] };
                roles.push(...claims.roles);
            }

            const user = {
                user_principal_name: account.username,
                display_name: account.name || "",
                isAdmin: !isProd || roles.includes("adminUser"),
                isPsh: roles.includes("pshUser"),
            };

            const [rumInitiated, setRum] = useState(false);
            const [accessToken, setAccessToken] = useState<string>("");
            function updateAccessToken(token: string) {
                sessionStorage.setItem("adal.idtoken", token);
                setAccessToken(token);
            }
            const history = useHistory();
            const navigationClient = new CustomNavigationClient(history);
            authInstance.setNavigationClient(navigationClient);

            useEffect(() => {
                if (!rumInitiated) {
                    datadogRum.init({
                        sessionSampleRate: 100,
                        site: "datadoghq.eu",
                        trackUserInteractions: true,
                        service: `hr-portal-front-end-${config.ENVIRONMENT}`,
                        applicationId: config.DD_APPLICATION_ID,
                        clientToken: config.DD_APPLICATION_TOKEN,
                        env: config.ENVIRONMENT,
                        version: config.VERSION,
                    });

                    datadogRum.setUser({
                        name: account.name,
                        email: account.username,
                    });

                    setRum(true);
                }
            }, [rumInitiated, account]);

            useEffect(() => {
                async function getAccessToken() {
                    if (accessToken) {
                        return;
                    }
                    await auth.instance
                        .acquireTokenSilent({
                            account,
                            scopes: [`${config.AAD_CLIENT_ID}/.default`],
                        })
                        .then((tokenResponse) => {
                            updateAccessToken(tokenResponse.accessToken);
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                return auth.instance.acquireTokenRedirect({
                                    scopes: [`${config.AAD_CLIENT_ID}/.default`],
                                    loginHint: account.username,
                                });
                            }
                            return;
                        });
                }
                getAccessToken();
            }, [accessToken]);

            return (
                <AuthContext.Provider value={{ account, user }}>{children}</AuthContext.Provider>
            );
        };

        const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

        root.render(
            <React.StrictMode>
                <MsalProvider instance={authInstance}>
                    <MsalAuthenticationTemplate
                        errorComponent={AuthError}
                        loadingComponent={AuthLoading}
                        interactionType={InteractionType.Redirect}
                    >
                        <AuthProvider>
                            <AppLoader apiBaseUrl={config.API_BASE_URL} />
                        </AuthProvider>
                    </MsalAuthenticationTemplate>
                </MsalProvider>
            </React.StrictMode>,
        );
    })
    .catch((err) => {
        console.log(err);
    });
